import React, { useRef, useState, useCallback } from "react";
import {
    Button,
    ButtonGroup,
    ButtonProps,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    ThemeProvider,
} from "@mui/material";
import { VPUITheme } from "../../theme/vpui-theme";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Popper from "@mui/material/Popper";

export interface IDropdownButtonProps extends ButtonProps {
    actionButtonLabel: string;
    defaultOption: IDropdownOptionItem;
    dropdownOptions: IDropdownOptionItem[];
    onDropdownOptionClick: (selectedOption: IDropdownOptionItem) => void;
}

export interface IDropdownOptionItem {
    label: string;
    value: string;
}

export const DropdownButton = (props: IDropdownButtonProps) => {
    const [openDropdown, setOpenDropdown] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [selectedDropdownOption, setSelectedDropdownOption] = useState(props.defaultOption.value);

    const handleDropdownOptionClick = useCallback((option: IDropdownOptionItem) => {
        setSelectedDropdownOption(option.value);
        setOpenDropdown(false);
        props.onDropdownOptionClick(option);
    }, []);

    const handleToggle = () => {
        setOpenDropdown(prevOpen => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setOpenDropdown(false);
    };

    return <>
        <ThemeProvider theme={VPUITheme}>
            <div>
                <ButtonGroup
                    sx={{
                        backgroundColor: "secondary.main",
                    }}
                    color={props.color}
                    variant={props.variant}
                    ref={anchorRef}
                    aria-label="Button group with a nested menu"
                >
                    <Button {...props}>{props.actionButtonLabel}</Button>
                    <Button
                        size="small"
                        aria-controls={openDropdown ? "split-button-menu" : undefined}
                        aria-expanded={openDropdown ? "true" : undefined}
                        aria-label="select merge strategy"
                        aria-haspopup="menu"
                        sx={{ padding: 0, minWidth: "30px !important"}}
                        onClick={handleToggle}
                    >
                        <ArrowDropDownIcon />
                    </Button>
                </ButtonGroup>
                <Popper
                    sx={{ zIndex: 1 }}
                    open={openDropdown}
                    placement="bottom-start"
                    anchorEl={anchorRef.current}
                    role={undefined}
                    transition
                    disablePortal
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin:
                                    placement === "bottom" ? "center top" : "center bottom",
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList id="split-button-menu" autoFocusItem>
                                        {props.dropdownOptions.map(option => (
                                            <MenuItem
                                                sx={{ minWidth: anchorRef?.current?.offsetWidth }}
                                                key={option.value}
                                                selected={option.value === selectedDropdownOption}
                                                onClick={() => handleDropdownOptionClick(option)}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </div>
        </ThemeProvider>
    </>;
};
